var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/InitiativeTracker/InitiativeTracker.less';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useApiInstance } from '@/api/index';
import CharacterDetailsSidebar from '@/components/Common/CharacterDetailsSidebar';
import DiceBar from '@/components/Common/DiceBar';
import CombatLogs from '@/components/InitiativeTracker/CombatLogs';
import InitiativeCard from '@/components/InitiativeTracker/InitiativeCard';
import InitiativeTrackerCardInfo from '@/components/InitiativeTracker/InitiativeTrackerCardInfo';
import InitiativeTrackerHeader from '@/components/InitiativeTracker/InitiativeTrackerHeader';
import LongRestButton from '@/components/InitiativeTracker/LongRestButton';
import ControlsContainer from '@/components/UI/ControlsContainer';
import Loader from '@/components/UI/Loader';
import { useApiContext } from '@/context/ApiContext';
import { useAuth } from '@/context/AuthContext';
import { useInitiativeTracker } from '@/context/InitiativeTrackerContext';
import { useCardsMemo } from '@/hooks/useCardsQueue';
import { useCenterActiveCard } from '@/hooks/useCenterActiveCard';
import { sortCards } from '@/utils/sort';
function getOnUpdateCharacterChangedKeys(prev, next) {
    const res = [];
    Object.keys(next).forEach((key) => {
        if (prev[key] !== next[key]) {
            res.push(key);
        }
    });
    return res;
}
const MemoInitiativeCard = memo(InitiativeCard);
const InitiativeTracker = () => {
    const { cards, setCards, selectedCardsIds, setSelectedCardsIds, activeCombatMap, setActiveCombatMap, setIsNextTurnLoading, loading, setLoading, isOwnerUser, setIsOwnerUser, } = useInitiativeTracker();
    const sortedCards = useCardsMemo(cards);
    const [showAddForm, setShowAddForm] = useState(false);
    const [sidebarCharacter, setSidebarCharacter] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const controlsContainerRef = useRef(null);
    const navigate = useNavigate();
    const { user, loading: isAuthLoading, userSettings } = useAuth();
    const { useMockApi } = useApiContext();
    // TODO FIXME: cardsRowRef => as React.RefObject<HTMLDivElement> => useCenterActiveCard props
    const cardsRowRef = useRef(null);
    const api = useApiInstance();
    const characterApiInstance = api.characterApiInstance;
    const combatTrackerApiInstance = api.combatTrackerApiInstance;
    /*
    -------------------
    API методы.
    -------------------
    */
    useEffect(() => {
        if (isAuthLoading)
            return;
        if (!user && !useMockApi) {
            setLoading(false);
            console.log('User is not authenticated');
            navigate('/auth');
            return;
        }
        // console.log('[InitiativeTracker] useMockApi:', useMockApi);
        combatTrackerApiInstance.combat_map
            .list({ is_active: true, created_by_id: user === null || user === void 0 ? void 0 : user.id })
            .then((data) => {
            var _a;
            // console.log('[InitiativeTracker] Active combat maps:', data);
            if (data.length > 0) {
                const combatMap = data[0];
                setActiveCombatMap(combatMap);
                setSelectedCardsIds([]);
                setCards(sortCards((_a = combatMap.characters) !== null && _a !== void 0 ? _a : []));
                setLoading(false);
            }
            else {
                // создать новую карту боя
                combatTrackerApiInstance.combat_map
                    .create({ name: 'New Combat Map', isActive: true })
                    .then((newCombatMap) => {
                    console.log('[InitiativeTracker] Created new combat map:', newCombatMap);
                    setActiveCombatMap(newCombatMap);
                    setSelectedCardsIds([]);
                    setLoading(false);
                })
                    .catch((err) => {
                    console.error('[InitiativeTracker] Error creating combat map:', err);
                });
            }
        })
            .catch((err) => {
            console.error('[InitiativeTracker] Error fetching active combat map:', err);
            setActiveCombatMap(null);
            setSelectedCardsIds([]);
        });
    }, [
        user,
        isAuthLoading,
        useMockApi,
        combatTrackerApiInstance.combat_map,
        setLoading,
        navigate,
        setActiveCombatMap,
        setSelectedCardsIds,
        setCards,
    ]);
    useEffect(() => {
        if (isAuthLoading)
            return;
        if (activeCombatMap && user) {
            setIsOwnerUser(user.id === activeCombatMap.createdBy);
        }
    }, [user, activeCombatMap, isAuthLoading, setIsOwnerUser]);
    // Удалить карточку по ID и обновить состояние.
    const handleDelete = (character) => __awaiter(void 0, void 0, void 0, function* () {
        // await characterApiInstance.delete(id);
        if (activeCombatMap) {
            yield combatTrackerApiInstance.combat_map.removeCharacterFromMap(activeCombatMap.id, character.id);
        }
        else {
            console.error('activeCombatMap is null, cannot remove character from map');
            return;
        }
        setCards((prev) => prev.filter((card) => card.id !== character.id));
    });
    // Обновить карточку и обновить состояние.
    const handleUpdate = (card) => __awaiter(void 0, void 0, void 0, function* () {
        const prevCard = cards.find((c) => c.id === card.id);
        if (!prevCard)
            return;
        const combatMapDataKeys = ['initiative', 'isActive'];
        const changedKeys = getOnUpdateCharacterChangedKeys(prevCard, card);
        const isOnlyCombatMapDataKeysChanged = changedKeys.every((k) => combatMapDataKeys.includes(k));
        const isOnlyCharacterDataKeysChanged = changedKeys.every((k) => !combatMapDataKeys.includes(k));
        let updated;
        if (isOnlyCombatMapDataKeysChanged) {
            updated = Object.assign(Object.assign({}, prevCard), card);
        }
        else {
            updated = yield characterApiInstance.update(card);
            updated.initiative = card.initiative || updated.initiative || 0;
            updated.isActive = card.isActive;
        }
        if (!isOnlyCharacterDataKeysChanged && activeCombatMap) {
            yield combatTrackerApiInstance.combat_map_character_data.update_character_data({
                combatMapId: activeCombatMap.id,
                characterId: updated.id,
                isActive: updated.isActive,
                initiative: updated.initiative,
            });
        }
        setCards((prev) => sortCards(prev.map((c) => (c.id === card.id ? Object.assign(Object.assign({}, card), updated) : c))));
    });
    const handleAdd = (character) => __awaiter(void 0, void 0, void 0, function* () {
        const newCard = yield characterApiInstance.create(character);
        newCard.initiative = character.initiative || 0;
        if (activeCombatMap) {
            yield combatTrackerApiInstance.combat_map.addCharacterToMap(activeCombatMap.id, newCard.id, newCard.initiative);
        }
        else {
            console.error('activeCombatMap is null, cannot update character data');
            return;
        }
        setCards((prev) => {
            const updated = [...prev, newCard];
            return sortCards(updated);
        });
    });
    const handleAddCardToMap = (character) => __awaiter(void 0, void 0, void 0, function* () {
        if (cards.some((c) => c.id === character.id))
            return;
        character.initiative = character.initiative || 0;
        if (activeCombatMap) {
            yield combatTrackerApiInstance.combat_map.addCharacterToMap(activeCombatMap.id, character.id, character.initiative);
        }
        else {
            console.error('activeCombatMap is null, cannot add character to map');
            return;
        }
        setCards((prev) => {
            if (prev.some((c) => c.id === character.id))
                return prev;
            const updated = [...prev, character];
            return sortCards(updated);
        });
    });
    /*
    -------------------
    LOCAL методы.
    -------------------
    */
    const toggleAddCardFormVisibility = () => setShowAddForm((v) => !v);
    const openCharacterSidebar = (char) => {
        setSidebarCharacter(char);
        setIsSidebarOpen(true);
        // prevent background scroll as other sidebars do
        try {
            document.body.style.overflow = 'hidden';
        }
        catch (e) {
            // ignore
        }
    };
    const closeCharacterSidebar = () => {
        setIsSidebarOpen(false);
        setSidebarCharacter(null);
        try {
            document.body.style.overflow = 'auto';
        }
        catch (e) {
            // ignore
        }
    };
    const debouncedSetActiveCard = useDebouncedCallback((cardId, mapId) => {
        combatTrackerApiInstance.combat_map_character_data
            .update_character_data({
            combatMapId: mapId,
            characterId: cardId,
            isActive: true,
        })
            .catch((err) => {
            console.error('[InitiativeTracker] setActiveCardByApi error:', err);
        })
            .finally(() => {
            setIsNextTurnLoading(false);
        });
    }, 1000);
    const handleNext = () => {
        if (cards.length > 1) {
            setCards((prev) => {
                const currentIdx = prev.findIndex((card) => card.isActive);
                if (currentIdx === -1) {
                    prev[0].isActive = true;
                    debouncedSetActiveCard(prev[0].id, activeCombatMap.id);
                    return sortCards(prev.map((card, idx) => (Object.assign(Object.assign({}, card), { isActive: idx === 0 }))));
                }
                let nextIdx = (currentIdx + 1) % prev.length;
                while (prev[nextIdx].health <= 0) {
                    nextIdx = (nextIdx + 1) % prev.length;
                    if (nextIdx === currentIdx) {
                        setIsNextTurnLoading(false);
                        return prev;
                    }
                }
                const nextCard = prev[nextIdx];
                debouncedSetActiveCard(nextCard.id, activeCombatMap.id);
                return prev.map((card, idx) => (Object.assign(Object.assign({}, card), { isActive: idx === nextIdx, divideDamage: false })));
            });
            setSelectedCardsIds([]);
        }
    };
    // Обработать выбор карточки, добавляя или удаляя ID из списка выделенных.
    const handleSelect = useCallback((id, event) => {
        if (event) {
            const target = event.target;
            if (target.closest('button'))
                return; // если клик по кнопке — не выделять
        }
        setSelectedCardsIds((prev) => prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]);
    }, [setSelectedCardsIds]);
    const handleChooseCombatMap = (map) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const updated = yield combatTrackerApiInstance.combat_map.update(map.id, { isActive: true });
            // const updated = await combatTrackerApiInstance.combat_map.get(map.id);
            setActiveCombatMap(updated);
            setSelectedCardsIds([]);
            setCards(sortCards(updated.characters || []));
        }
        catch (e) {
            console.error('[InitiativeTracker] Error choosing combat map:', e);
        }
    });
    useCenterActiveCard(cards, cardsRowRef);
    const onSetDivideDamage = (card, value) => {
        setCards((prev) => prev.map((c) => (Object.assign(Object.assign({}, c), { divideDamage: c.id === card.id ? value : c.divideDamage }))));
    };
    const onSetMultiplyDamage = (card, value) => {
        setCards((prev) => prev.map((c) => (Object.assign(Object.assign({}, c), { multiplyDamage: c.id === card.id ? value : c.multiplyDamage }))));
    };
    const handleLongRestUpdated = useCallback((updatedCharacters) => {
        setCards((prev) => sortCards(prev.map((c) => {
            const patch = updatedCharacters.find((u) => u.id === c.id);
            return patch ? Object.assign(Object.assign({}, c), patch) : c;
        })));
    }, [setCards]);
    if (loading)
        return _jsx(Loader, {});
    return (_jsxs("div", { className: "initiative-tracker-container", children: [_jsx(ControlsContainer, { ref: controlsContainerRef, position: "bottom-left", direction: "column" }), _jsxs("div", { className: "initiative-tracker-wrapper", children: [_jsxs("div", { className: "initiative-tracker", children: [_jsx(InitiativeTrackerHeader, { user: user, handleNext: handleNext, toggleAddCardFormVisibility: toggleAddCardFormVisibility, showAddForm: showAddForm, handleAddCard: handleAdd, handleUpdateCard: handleUpdate, handleChooseCombatMap: handleChooseCombatMap, handleAddCardToMap: handleAddCardToMap, pageButtonsContainer: controlsContainerRef.current }), _jsx("div", { className: "cards-row", ref: cardsRowRef, children: sortedCards.map((card) => (_jsx(MemoInitiativeCard, { card: card, onDelete: handleDelete, onUpdate: handleUpdate, onSelect: handleSelect, onOpenDetails: openCharacterSidebar, isSelected: selectedCardsIds.includes(card.id), isOwnerUser: isOwnerUser, userSettings: userSettings, onSetDivideDamage: onSetDivideDamage, onSetMultiplyDamage: onSetMultiplyDamage }, card.id))) })] }), activeCombatMap && (_jsx(CombatLogs, { combatMap: activeCombatMap, pageButtonsContainer: controlsContainerRef.current }))] }), _jsx(InitiativeTrackerCardInfo, { character: cards.find((card) => card.isActive), onUpdate: handleUpdate, pageButtonsContainer: controlsContainerRef.current }), isOwnerUser && (_jsx(LongRestButton, { pageButtonsContainer: controlsContainerRef.current, show: true, onUpdated: handleLongRestUpdated })), isSidebarOpen &&
                sidebarCharacter &&
                createPortal(_jsx(CharacterDetailsSidebar, { character: sidebarCharacter, isOpen: isSidebarOpen, onClose: closeCharacterSidebar, onSave: handleUpdate, onDelete: handleDelete, combatMap: activeCombatMap }), document.body), _jsx(DiceBar, { pageButtonsContainer: controlsContainerRef.current })] }));
};
export default InitiativeTracker;
